import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip, Upload, Download, Trash2, Eye, FileText, Image as ImageIcon, File as FileIcon } from 'lucide-react';
import { userDocumentsApi, UserDocument } from '../../services/api';
import { useToast } from '../Toast/Toast';

/**
 * Documents on the user master — ID proof, PAN/Aadhaar, offer letter, signed
 * policies. Admin only, enforced on the server.
 *
 * Works in two modes, like the voucher attachments:
 *  - Existing user (userId set): files upload immediately and are listed live.
 *  - New user (userId null): there is no id to attach to yet, so files are
 *    STAGED in the parent's state and uploaded right after the user is created
 *    (see uploadStagedUserDocuments).
 */

export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_PER_USER = 25;

export const fmtSize = (n: number): string => {
    if (!n) return '0 B';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/** Uploads documents staged while the user had no id yet. Returns how many went
 *  up; never throws, so a failed upload can't undo a created user. */
export async function uploadStagedUserDocuments(userId: string, files: File[]): Promise<number> {
    if (!userId || !files?.length) return 0;
    try {
        const res = await userDocumentsApi.upload(userId, files);
        return res?.data?.saved ?? files.length;
    } catch {
        return 0;
    }
}

const iconFor = (name: string, mime: string | null) => {
    const m = (mime || '').toLowerCase();
    const ext = name.split('.').pop()?.toLowerCase() || '';
    if (m.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) {
        return <ImageIcon size={15} className="text-purple-500" />;
    }
    if (m === 'application/pdf' || ext === 'pdf') return <FileText size={15} className="text-red-500" />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileText size={15} className="text-green-600" />;
    if (['doc', 'docx'].includes(ext)) return <FileText size={15} className="text-blue-600" />;
    return <FileIcon size={15} className="text-gray-400" />;
};

const canPreview = (name: string, mime: string | null) => {
    const m = (mime || '').toLowerCase();
    const ext = name.split('.').pop()?.toLowerCase() || '';
    return m.startsWith('image/') || m === 'application/pdf'
        || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'pdf'].includes(ext);
};

interface Props {
    /** User id once saved; null while creating a new user. */
    userId: string | null;
    /** Files chosen before the user exists. Owned by the parent so it can
     *  upload them after create. */
    stagedFiles: File[];
    onStagedChange: (files: File[]) => void;
}

const UserDocuments: React.FC<Props> = ({ userId, stagedFiles, onStagedChange }) => {
    const { showSuccess, showError } = useToast();
    const [list, setList] = useState<UserDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploadPct, setUploadPct] = useState<number | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [confirmDel, setConfirmDel] = useState<UserDocument | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const total = list.length + stagedFiles.length;

    const refresh = useCallback(async () => {
        if (!userId) { setList([]); return; }
        setLoading(true);
        try {
            const res = await userDocumentsApi.list(userId);
            if (res.success) setList(res.data);
        } catch {
            /* keep whatever is on screen — the count still reflects it */
        }
        setLoading(false);
    }, [userId]);

    useEffect(() => { refresh(); }, [refresh]);

    const addFiles = async (picked: FileList | File[] | null) => {
        const files = Array.from(picked || []);
        if (!files.length) return;

        const tooBig = files.filter(f => f.size > MAX_FILE_BYTES);
        if (tooBig.length) {
            showError('File too large', `${tooBig.map(f => f.name).join(', ')} — limit is ${fmtSize(MAX_FILE_BYTES)} per file.`);
        }
        const ok = files.filter(f => f.size <= MAX_FILE_BYTES);
        if (!ok.length) return;
        if (total + ok.length > MAX_PER_USER) {
            showError('Too many documents', `A user can hold at most ${MAX_PER_USER} documents.`);
            return;
        }

        // No user id yet → stage locally, upload after the user is created.
        if (!userId) {
            onStagedChange([...stagedFiles, ...ok]);
            return;
        }

        setUploadPct(0);
        try {
            await userDocumentsApi.upload(userId, ok, undefined, setUploadPct);
            showSuccess('Attached', `${ok.length} document(s) attached.`);
            await refresh();
        } catch (err: any) {
            showError('Upload failed', err?.message || 'Could not attach the document.');
        }
        setUploadPct(null);
    };

    const doDelete = async (doc: UserDocument) => {
        setConfirmDel(null);
        try {
            await userDocumentsApi.remove(doc.id);
            showSuccess('Removed', `${doc.original_name} was removed.`);
            await refresh();
        } catch (err: any) {
            showError('Remove failed', err?.message || 'Could not remove the document.');
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <Paperclip size={15} className="text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-800">Documents</h3>
                {total > 0 && (
                    <span className="min-w-[20px] px-1.5 rounded-full bg-blue-600 text-white text-[10px] leading-[18px] text-center font-bold">
                        {total}
                    </span>
                )}
            </div>

            <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                    dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
            >
                <Upload size={20} className="mx-auto text-gray-400 mb-1" />
                <div className="text-sm text-gray-600">Drop files here, or click to browse</div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                    ID proof, PAN, offer letter… up to {MAX_PER_USER} files, {fmtSize(MAX_FILE_BYTES)} each
                </div>
                <input
                    ref={inputRef} type="file" multiple className="hidden"
                    onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
                />
            </div>

            {uploadPct !== null && (
                <div className="h-1.5 bg-gray-200 rounded overflow-hidden">
                    <div className="h-full bg-blue-600 transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
            )}

            {!userId && (
                <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    These files will be attached once the user is created.
                </div>
            )}

            <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                {loading && <div className="p-4 text-center text-sm text-gray-400">Loading…</div>}

                {!loading && total === 0 && (
                    <div className="p-5 text-center text-sm text-gray-400">No documents attached yet.</div>
                )}

                {list.map(doc => (
                    <div key={doc.id} className="flex items-center gap-2 px-3 py-2">
                        {iconFor(doc.original_name, doc.mime_type)}
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-800 truncate" title={doc.original_name}>{doc.original_name}</div>
                            <div className="text-[11px] text-gray-400">
                                {fmtSize(doc.size)}
                                {doc.uploaded_by ? ` · ${doc.uploaded_by}` : ''}
                                {doc.created_at ? ` · ${new Date(doc.created_at).toLocaleDateString('en-GB')}` : ''}
                            </div>
                        </div>
                        {canPreview(doc.original_name, doc.mime_type) && (
                            <button type="button" title="Preview"
                                onClick={() => userDocumentsApi.preview(doc.id).catch((e: any) => showError('Preview failed', e?.message || 'Could not open'))}
                                className="text-gray-400 hover:text-blue-600 p-1"><Eye size={15} /></button>
                        )}
                        <button type="button" title="Download"
                            onClick={() => userDocumentsApi.download(doc.id, doc.original_name).catch((e: any) => showError('Download failed', e?.message || 'Could not download'))}
                            className="text-gray-400 hover:text-blue-600 p-1"><Download size={15} /></button>
                        <button type="button" title="Remove" onClick={() => setConfirmDel(doc)}
                            className="text-gray-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                    </div>
                ))}

                {stagedFiles.map((f, i) => (
                    <div key={`staged-${i}`} className="flex items-center gap-2 px-3 py-2 bg-amber-50/50">
                        {iconFor(f.name, f.type)}
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-800 truncate" title={f.name}>{f.name}</div>
                            <div className="text-[11px] text-amber-600">{fmtSize(f.size)} · pending save</div>
                        </div>
                        <button type="button" title="Remove"
                            onClick={() => onStagedChange(stagedFiles.filter((_, idx) => idx !== i))}
                            className="text-gray-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                    </div>
                ))}
            </div>

            {confirmDel && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[70]">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-4">
                        <h3 className="text-base font-semibold text-gray-800">Remove document?</h3>
                        <p className="text-sm text-gray-600 mt-1 break-all">{confirmDel.original_name}</p>
                        <p className="text-xs text-gray-500 mt-1">The file is deleted from the server and cannot be recovered.</p>
                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" onClick={() => setConfirmDel(null)}
                                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                            <button type="button" onClick={() => doDelete(confirmDel)}
                                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded">Remove</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserDocuments;
